import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Composition,
  CompositionUpdateBody,
  CompositionComponentUpdate,
  CompositionProcessUpdate,
  ProductLookupItem, // Import ProductLookupItem
  ProcessLookupItem, // Import ProcessLookupItem
  Surcharge, // Import Surcharge type
  CostPreviewResponse // NEW: Import CostPreviewResponse
} from '../types/costwand';
import { useComposites } from '../hooks/useComposites.ts';
import { useSurchargeLookup } from '../hooks/useSurchargeLookup.ts'; // NEW: Import useSurchargeLookup
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import CompositionEditComponent from '../components/CompositionEditComponent.tsx';
import { apiClient, ApiError } from '../api/apiClient.tsx'; // Import apiClient and ApiError
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx'; // Import Select components
import { Label } from '../components/ui/label.tsx'; // Import Label
import { useProduct } from '../hooks/useProduct.ts';
import { Spinner } from '../components/ui/spinner.tsx'; // Import Spinner for loading states
import { useError } from '../context/ErrorContext.tsx'; // NEW: Import useError for global error display
import { useProductAndCompositeLookup } from '../hooks/useProductAndCompositeLookup.ts'; // NEW: Import combined lookup hook

import { useNavigate } from 'react-router-dom';

import { useWhitelabelContext } from '../context/WhitelabelContext.tsx';

const CompositionEditPage: React.FC = () => {
  const { whitelabelConfig } = useWhitelabelContext();

  const { id } = useParams<{ id: string }>();
  const { composites, updateCompositeComposition, loading, error, fetchComposites } = useComposites();
  const { surcharges, loading: surchargesLoading, error: surchargesError } = useSurchargeLookup();
  const { displayError } = useError(); // NEW: Use global error context

  const [compositionData, setCompositionData] = useState<CompositionUpdateBody | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  const { updateProduct } = useProduct(); // Only need updateProduct here

  const navigate = useNavigate();

  // NEW: Use combined product and composite lookup
  const { lookupItems, loading: productCompositeLookupLoading, error: productCompositeLookupError } = useProductAndCompositeLookup();
  // Processes still need to be fetched separately
  const [processes, setProcesses] = useState<ProcessLookupItem[]>([]);
  const [processesLookupLoading, setProcessesLookupLoading] = useState<boolean>(true);
  const [processesLookupError, setProcessesLookupError] = useState<string | null>(null);

  // NEW: State for calculated cost without surcharge and current surcharge percentage
  const [baseCostWithoutSurcharge, setBaseCostWithoutSurcharge] = useState<number | null>(null);
  const [currentSurchargePercentage, setCurrentSurchargePercentage] = useState<number | null>(null);

  // NEW: State for preview cost
  const [previewCost, setPreviewCost] = useState<number | null>(null); // This will hold cost WITHOUT surcharge
  const [previewCostWithSurcharge, setPreviewCostWithSurcharge] = useState<string | null>(null); // NEW: State for cost WITH surcharge
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Debounce timer for preview save
  const previewTimerRef = useRef<NodeJS.Timeout | null>(null);
  // NEW: Ref to store the last successfully previewed composition data
  const lastSuccessfullyPreviewedCompositionDataRef = useRef<CompositionUpdateBody | null>(null);

  const emptyCompositionData = { components: [], processes: [] };

  const getEntityTitle = (entityId: string, defaultTitle: string) => 
    whitelabelConfig.entities?.[entityId].title_singular || defaultTitle;

  // Fetch processes for dropdowns
  useEffect(() => {
    const fetchProcessesData = async () => {
      setProcessesLookupLoading(true);
      try {
        const fetchedProcesses = await apiClient<ProcessLookupItem[]>('/processes', { allPages: true });
        setProcesses(fetchedProcesses.data);
      } catch (err: any) {
        setProcessesLookupError(err.message || 'Failed to load processes lookup data.');
        displayError(err.message || 'Failed to load processes lookup data.');
      } finally {
        setProcessesLookupLoading(false);
      }
    };
    fetchProcessesData();
  }, [displayError]);

  useEffect(() => {
    if (id && composites && surcharges) {
      const composite = composites.find((c) => String(c.id) === id);
      if (composite) {
        const initialCompData: CompositionUpdateBody = {
          components: composite.composition.components.map(c => ({
            component_id: c.component_id,
            overhead_charge: c.overhead_charge,
            required_qty: c.required_qty,
            unit_dimension: c.unit_dimension,
            _labels: c._labels,
            processes: c.processes ? c.processes.map(p => ({
              process_id: p.process_id,
              cost: p.cost,
              required_qty: p.required_qty,
              _labels: p._labels,
            })) : [],
          })),
          processes: composite.composition.processes ? composite.composition.processes.map(p => ({
            process_id: p.process_id,
            cost: p.cost,
            required_qty: p.required_qty,
            _labels: p._labels,
          })) : [],
        };
        setCompositionData(initialCompData);
        // Set the initial valid state for undo purposes
        lastSuccessfullyPreviewedCompositionDataRef.current = initialCompData;

        // NEW: Calculate base cost without surcharge
        if (composite.cost !== undefined && composite.cost !== null) {
          const surchargeProfile = surcharges.find(s => s.id === composite.tax_profile_id);
          const percentage = surchargeProfile?.percentage || 0;
          setCurrentSurchargePercentage(percentage);
          const calculatedBaseCost = composite.cost / (1 + percentage / 100);
          setBaseCostWithoutSurcharge(calculatedBaseCost);
        } else {
          setBaseCostWithoutSurcharge(null);
          setCurrentSurchargePercentage(null);
        }

      } else {
        // If composite not found, refetch to ensure data is current
        //fetchComposites(); // This might cause a loop if ID is truly invalid
      }
    }
  }, [id, composites, surcharges, fetchComposites]);

  // NEW: Debounced preview save effect
  useEffect(() => {
    if (!id || !compositionData) return;

    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }

    setPreviewLoading(true); // Indicate that a preview calculation is pending
    setPreviewError(null);

    previewTimerRef.current = setTimeout(() => {
      handlePreviewSave(compositionData);
    }, 700); // Debounce for 700ms

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    };
  }, [id, compositionData]); // Trigger when compositionData changes

  // NEW: Function to handle the preview save
  const handlePreviewSave = useCallback(async (data: CompositionUpdateBody) => {
    if (!id) return;

    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const previewEndpoint = `/composites/${id}?_preview=1`;
      const response = await apiClient<CostPreviewResponse>(previewEndpoint, {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      if (response.cost === null) {
        // If cost is null, don't show an error, just clear preview data
        setPreviewCost(null);
        setPreviewCostWithSurcharge(null);
        setPreviewError(null);
        return; // Exit early, don't process further
      }

      // If successful, update the last known good state
      lastSuccessfullyPreviewedCompositionDataRef.current = data;

      const composite = composites.find((c) => String(c.id) === id);
      const surchargeProfile = surcharges.find(s => s.id === composite?.tax_profile_id); // Use optional chaining for composite
      const percentage = surchargeProfile?.percentage || 0;

      const costWithSurcharge = response.cost.toFixed(2);
      const costWithOutSurcharge = parseFloat(costWithSurcharge) / (1 + percentage / 100); // Parse to float before division

      setPreviewCost(costWithOutSurcharge); // This is the cost WITHOUT surcharge
      setPreviewCostWithSurcharge(costWithSurcharge); // This is the cost WITH surcharge
    } catch (err: any) {
      setPreviewError(err.message || 'Failed to get preview cost.');
      displayError(err.message || 'Failed to get preview cost.'); // NEW: Display global error
      setPreviewCost(null);
      setPreviewCostWithSurcharge(null); // NEW: Clear surcharge preview cost on error

      // NEW: UNDO the last action by reverting to the last successfully previewed state
      if (lastSuccessfullyPreviewedCompositionDataRef.current) {
        console.warn("Preview failed, reverting to last valid composition state.");
        setCompositionData(lastSuccessfullyPreviewedCompositionDataRef.current);
        // The useEffect for debounced preview will automatically re-trigger handlePreviewSave
        // with the reverted state.
      } else {
        //console.error("Preview failed and no last valid state to revert to.");
        setCompositionData(emptyCompositionData);
        // If there's no last valid state (e.g., first preview failed),
        // we might want to clear the form or show a more specific error.
      }
    } finally {
      setPreviewLoading(false);
    }
  }, [id, displayError, composites, surcharges]); // NEW: Add surcharges to dependencies

  // This function will be called by all modification handlers
  const updateCompositionState = useCallback((updater: (prev: CompositionUpdateBody | null) => CompositionUpdateBody) => {
    setCompositionData((prev) => {
      const newComposition = updater(prev);
      return newComposition;
    });
  }, []);

  const handleComponentChange = useCallback((index: number, field: string, value: any) => {
    updateCompositionState((prev) => {
      if (!prev) prev = { components: [], processes: [] };
      const newComponents = [...prev.components];
      newComponents[index] = {
        ...newComponents[index],
        [field]: value,
      };
      return { ...prev, components: newComponents };
    });
  }, [updateCompositionState]);

  const handleProcessChange = useCallback((componentIndex: number, processIndex: number, field: string, value: any) => {
    updateCompositionState((prev) => {
      if (!prev) prev = { components: [], processes: [] };
      const newComponents = [...prev.components];
      const newProcesses = [...newComponents[componentIndex].processes];
      newProcesses[processIndex] = {
        ...newProcesses[processIndex],
        [field]: value,
      };
      newComponents[componentIndex].processes = newProcesses;
      return { ...prev, components: newComponents };
    });
  }, [updateCompositionState]);

  const handleRemoveComponent = useCallback((index: number) => {
    updateCompositionState((prev) => {
      if (!prev) prev = { components: [], processes: [] };
      const newComponents = [...prev.components];
      newComponents.splice(index, 1);
      return { ...prev, components: newComponents };
    });
  }, [updateCompositionState]);

  // NEW: State for the "Add New Component" dropdown
  const [selectedAddComponentId, setSelectedAddComponentId] = useState<string>('');
  const handleRemoveProcess = useCallback((componentIndex: number, processIndex: number) => {
    setCompositionData((prev) => {
      if (!prev) prev = {components:[], processes: []};
      const newComponents = [...prev.components];
      const newProcesses = [...newComponents[componentIndex].processes];
      newProcesses.splice(processIndex, 1);
      newComponents[componentIndex].processes = newProcesses;
      const updatedComposition = { ...prev, components: newComponents };
      return updatedComposition;
    });
  }, []);

  const handleAddComponent = useCallback((component: any, component_labels: any, unit_dimension: string) => {
    updateCompositionState((prev) => {
      if (!prev) prev = { components: [], processes: [] };
      const newComponent: CompositionComponentUpdate = {
        component_id: component.id,
        overhead_charge: 0,
        required_qty: 1,
        unit_dimension: component.unit_dimension,
        _labels: component_labels,
        processes: [],
      };

      // Prepend new component
      return { ...prev, components: [newComponent, ...prev.components] };
    });
    setSelectedAddComponentId(''); // NEW: Reset dropdown after adding
  }, [updateCompositionState]);

  const handleAddProcessToComponent = useCallback((componentIndex: number, process_id: number, process_labels: any) => {
    updateCompositionState((prev) => {
      if (!prev) prev = { components: [], processes: [] };
      const newComponents = [...prev.components];
      const newProcess: CompositionProcessUpdate = {
        process_id: process_id,
        cost: 0,
        required_qty: 1,
        _labels: process_labels,
      };
      // Prepend new process to component's processes
      newComponents[componentIndex].processes = [newProcess, ...newComponents[componentIndex].processes];
      return { ...prev, components: newComponents };
    });
  }, [updateCompositionState]);

  // --- New Handlers for Main Processes ---
  const handleMainProcessChange = useCallback((index: number, field: string, value: any) => {
    updateCompositionState((prev) => {
      if (!prev) prev = { components: [], processes: [] };
      const newProcesses = [...prev.processes];
      newProcesses[index] = {
        ...newProcesses[index],
        [field]: value,
      };
      return { ...prev, processes: newProcesses };
    });
  }, [updateCompositionState]);

  const handleRemoveMainProcess = useCallback((index: number) => {
    updateCompositionState((prev) => {
      if (!prev) prev = { components: [], processes: [] };
      const newProcesses = [...prev.processes];
      newProcesses.splice(index, 1);
      return { ...prev, processes: newProcesses };
    });
  }, [updateCompositionState]);
  // --- End New Handlers ---

  // NEW: State for the "Add Main Process" dropdown
  const [selectedAddMainProcessId, setSelectedAddMainProcessId] = useState<string>('');

  const handleAddMainProcess = useCallback((process_id: number, process_labels: any) => {
    updateCompositionState((prev) => {
      if (!prev) prev = { components: [], processes: [] };
      const newProcess: CompositionProcessUpdate = {
        process_id: process_id,
        cost: 0,
        required_qty: 1,
        _labels: process_labels,
      };
      // Prepend new main process
      return { ...prev, processes: [newProcess, ...prev.processes] };
    });
    setSelectedAddMainProcessId(''); // NEW: Reset dropdown after adding
  }, [updateCompositionState]);

  const handleSubmit = useCallback(async () => {
    if (!id || !compositionData) return;

    setFormSubmitting(true);
    try {
      const success = await updateCompositeComposition(parseInt(id, 10), compositionData);
      if (success) {
        alert('Composition updated successfully!');
        fetchComposites(); // Refetch to get updated data
      } else {
        // Error already displayed by global handler
      }
    } catch (err: any) {
      displayError(`Error updating composition: ${err.message || err}`); // NEW: Display global error
    } finally {
      setFormSubmitting(false);
    }
  }, [id, compositionData, updateCompositeComposition, fetchComposites, displayError]);

  const handleConvertBackToProduct = useCallback(async () => {
    setFormSubmitting(true);
    try {
      // Assuming unit_of_measure_id: 1 means converting back to a standard product
      const success = await updateProduct(parseInt(id!, 10), { unit_of_measure_id: 1 }); // Pass id as number
      if (success) {
        alert('Product updated successfully!');

      } else {
        // Error already displayed by global handler
      }
    } catch (err: any) {
      displayError(`Error converting product: ${err.message || err}`); // NEW: Display global error
    } finally {
      navigate('/products');
    }
  }, [id, updateProduct, navigate, displayError]);

  // Combine all loading and error states for a comprehensive check
  const overallLoading = loading || productCompositeLookupLoading || processesLookupLoading || surchargesLoading;
  const overallError = error || productCompositeLookupError || processesLookupError || surchargesError;

  if (overallLoading) return (
    <div className="flex justify-center items-center h-64">
      <Spinner size="lg" />
      <p className="ml-2 text-muted-foreground">Loading composition data...</p>
    </div>
  );
  if (overallError) return <div className="p-4 text-destructive">Error: {overallError}</div>;

  const currentComposite = composites.find((c) => String(c.id) === id) || { cost: 0 };
  // Using lookupItems to find the product/composite description for display
  const currentItemDescription = lookupItems.find(item => String(item.id) === id)?.description || `ID: ${id}`;

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-2 text-center">Edit Composition for {currentItemDescription}</h2>

      {currentComposite.cost !== undefined && currentComposite.cost !== null && (
        <div className="text-center text-lg font-medium mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md">
          {baseCostWithoutSurcharge !== null && (
            <p>Current Cost (without Surcharge): <span className="font-bold text-blue-700">${baseCostWithoutSurcharge.toFixed(2)}</span></p>
          )}
          <p>Current Cost (with {currentSurchargePercentage?.toFixed(2) || 'N/A'}% Surcharge): ${currentComposite.cost.toFixed(2)}</p>
        </div>
      )}
      {/* NEW: Display Preview Cost */}
      {(previewLoading || ((previewCost ?? 0).toFixed(2) !== (baseCostWithoutSurcharge ?? 0).toFixed(2))) ? (
        <div className="text-center text-lg font-medium mb-4 p-2 bg-green-50 border border-green-200 rounded-md">
          {previewLoading ? (
            <span className="flex items-center justify-center gap-2 text-green-700">
              <Spinner size="sm" /> Calculating preview cost...
            </span>
          ) : previewError ? (
            <p className="text-destructive">Preview Error: {previewError}</p>
          ) : previewCost !== null && previewCostWithSurcharge !== null ? (
            <>
              <p>Preview Cost (without Surcharge): <span className="font-bold text-green-700">${(previewCost ?? 0).toFixed(2)}</span></p>
              <p>Preview Cost (with {currentSurchargePercentage?.toFixed(2) || 'N/A'}% Surcharge): <span className="font-bold text-green-700">${previewCostWithSurcharge}</span></p>
            </>
          ) : (
            <p className="text-muted-foreground">Adjust values to see a preview cost.</p>
          )}
        </div>
      ) : ''}
      {/* Only show "Convert back to standard product" if it's currently a composite (unit_of_measure_id is null) */}
      {!currentComposite.unit_of_measure_id && !compositionData && (
        <Button variant="destructive" onClick={handleConvertBackToProduct} className="mb-4">
          Convert back to standard product
        </Button>
      )}

      {/* Main Composite Processes */}
      <h3 className="text-xl font-semibold mb-4">Main Processes:</h3>
      {/* Move "Add New Main Process" dropdown to the top */}
      <div className="mt-2 mb-4"> {/* Added mb-4 for spacing */}
        <Label htmlFor={`add_main_process_select`} className="block text-sm font-medium text-foreground mb-1">Add New Main Process</Label>
        <Select
          onValueChange={(value) => {
            setSelectedAddMainProcessId(value); // Update local state
            const selectedProcess = processes.find(p => String(p.id) === value);
            if (selectedProcess) {
              handleAddMainProcess(selectedProcess.id, { process_id: selectedProcess.description });
            }
          }}
          value={selectedAddMainProcessId} // Control the value
          disabled={processesLookupLoading}
        >
          <SelectTrigger id={`add_main_process_select`}>
            <SelectValue placeholder={processesLookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading processes...</span> : "Select a process to add"} />
          </SelectTrigger>
          <SelectContent>
            {processes.filter((item) => {
              if(!(compositionData || emptyCompositionData).processes) return false;

              let exists = (compositionData || emptyCompositionData).processes.map((process) => {
                if(process.process_id == item.id) {
                  return true;
                }
              }).indexOf(true) > -1;
              return !exists;
            }).map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {(compositionData || emptyCompositionData).processes.map((process, index) => (
        <div key={index} className="mb-2 p-2 border rounded-md bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> {/* Adjusted grid for better responsiveness */}
            <div>
              <Label htmlFor={`main_process_id_${index}`}>Process</Label>
              <Select
                onValueChange={(value) => {
                  const selectedProcess = processes.find(p => String(p.id) === value);
                  if (selectedProcess) {
                    handleMainProcessChange(index, 'process_id', selectedProcess.id);
                    handleMainProcessChange(index, '_labels', { process_id: selectedProcess.description });
                  }
                }}
                value={String(process.process_id)}
                disabled={processesLookupLoading}
              >
                <SelectTrigger id={`main_process_id_${index}`}>
                  <SelectValue placeholder="Select a process">
                    {process._labels?.process_id || `ID: ${process.process_id}`}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {processes.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor={`main_cost_${index}`}>Cost</Label>
              <Input
                type="number"
                id={`main_cost_${index}`}
                value={String(process.cost)}
                onChange={(e) => handleMainProcessChange(index, 'cost', parseFloat(e.target.value))}
                disabled={processesLookupLoading} // Disable input while loading
              />
            </div>
            <div>
              <Label htmlFor={`main_required_qty_${index}`}>Required Quantity</Label>
              <Input
                type="number"
                id={`main_required_qty_${index}`}
                value={String(process.required_qty)}
                onChange={(e) => handleMainProcessChange(index, 'required_qty', parseFloat(e.target.value))}
                disabled={processesLookupLoading} // Disable input while loading
              />
            </div>
          </div>
          <Button variant="destructive" size="sm" onClick={() => handleRemoveMainProcess(index)} className="mt-2">
            Delete Main Process
          </Button>
        </div>
      ))}
      
      {/* Separator between processes and components */}
      <hr className="my-8 border-t-2 border-gray-200" />

      {/* Components */}
      <h3 className="text-xl font-semibold mb-4">Components:</h3>
      {/* Move "Add New Component" dropdown to the top */}
      <div className="mt-2 mb-4"> {/* Added mb-4 for spacing */}
        <Label htmlFor="add_component_select" className="block text-sm font-medium text-foreground mb-1">Add New Component</Label>
        <Select
          onValueChange={(value) => {
            setSelectedAddComponentId(value); // Update local state
            const selectedItem = lookupItems.find(p => String(p.id) === value);
            if (selectedItem) {
              handleAddComponent(selectedItem, { component_id: selectedItem.description });
            }
          }}
          value={selectedAddComponentId} // Control the value
          disabled={productCompositeLookupLoading}
        >
          <SelectTrigger id="add_component_select">
            <SelectValue placeholder={productCompositeLookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading products/composites...</span> : `Select a ${getEntityTitle('products', 'product')} or ${getEntityTitle('composites', 'composite')} to add as a component`} />
          </SelectTrigger>
          <SelectContent>
            {lookupItems.filter((item) => item.id != (currentComposite.id || 0)).filter((item) => {
              let exists = (compositionData || emptyCompositionData).components.map((component) => {
                if(component.component_id == item.id) {
                  return true;
                }
              }).indexOf(true) > -1;
              return !exists;
            }).map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {(compositionData || emptyCompositionData).components.map((component, index) => (
        <CompositionEditComponent
          key={index}
          componentIndex={index}
          component={component}
          onComponentChange={handleComponentChange}
          onRemoveComponent={handleRemoveComponent}
          onProcessChange={handleProcessChange}
          onRemoveProcess={handleRemoveProcess}
          onAddProcess={handleAddProcessToComponent} // Pass the new handler
          products={lookupItems} // Pass combined lookup items for component selection
          processes={processes} // Pass processes for process selection
          lookupLoading={productCompositeLookupLoading || processesLookupLoading}
        />
      ))}
      

      <div className="flex justify-end space-x-2 mt-6">
        <Button asChild>
          <Link to="/products">Back to Products</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/composites">Back to Composites</Link>
        </Button>
        <Button onClick={handleSubmit} disabled={formSubmitting || overallLoading}>
          {formSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default CompositionEditPage;